import { IconButton, usePromptModal } from '@hive/component';
import { useRequireSubscription } from '@hive/core/components/hooks/use-require-subscription';
import { createEmptyCollection } from '@hive/core/components/page-list/use-collection-manager';
import { CollectionService } from '@hive/core/modules/collection';
import { ExplorerTreeRoot } from '@hive/core/modules/explorer/views/tree';
import { WorkbenchService } from '@hive/core/modules/workbench';
import { useI18n } from '@hive/i18n';
import { track } from '@hive/track';
import { AddCollectionIcon } from '@blocksuite/icons/rc';
import { useLiveData, useServices } from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { useCallback } from 'react';

import { ExplorerService } from '../../../services/explorer';
import { CollapsibleSection } from '../../layouts/collapsible-section';
import { ExplorerCollectionNode } from '../../nodes/collection';
import { RootEmpty } from './empty';
import * as styles from './index.css';

export const ExplorerCollections = () => {
  const t = useI18n();
  const { collectionService, workbenchService, explorerService } = useServices({
    CollectionService,
    WorkbenchService,
    ExplorerService,
  });
  const explorerSection = explorerService.sections.collections;
  const collections = useLiveData(collectionService.collections$);
  const { openPromptModal } = usePromptModal();
  const { isFreeUser, showUpgradeModal } = useRequireSubscription();

  const handleCreateCollection = useCallback(() => {
    if (isFreeUser) {
      showUpgradeModal();
      return;
    }
    openPromptModal({
      title: t['com.hive.editCollection.saveCollection'](),
      label: t['com.hive.editCollectionName.name'](),
      inputOptions: {
        placeholder: t['com.hive.editCollectionName.name.placeholder'](),
      },
      children: (
        <div className={styles.createTips}>
          {t['com.hive.editCollectionName.createTips']()}
        </div>
      ),
      confirmText: t['com.hive.editCollection.save'](),
      cancelText: t['com.hive.editCollection.button.cancel'](),
      confirmButtonOptions: {
        variant: 'primary',
      },
      onConfirm(name) {
        const id = nanoid();
        collectionService.addCollection(createEmptyCollection(id, { name }));
        track.$.navigationPanel.organize.createOrganizeItem({
          type: 'collection',
        });
        workbenchService.workbench.openCollection(id);
        explorerSection.setCollapsed(false);
      },
    });
  }, [
    collectionService,
    explorerSection,
    isFreeUser,
    openPromptModal,
    showUpgradeModal,
    t,
    workbenchService.workbench,
  ]);

  return (
    <CollapsibleSection
      name="collections"
      testId="explorer-collections"
      title={t['com.hive.rootAppSidebar.collections']()}
      actions={
        <IconButton
          data-testid="explorer-bar-add-collection-button"
          onClick={handleCreateCollection}
          size="16"
          tooltip={t[
            'com.hive.rootAppSidebar.explorer.collection-section-add-tooltip'
          ]()}
        >
          <AddCollectionIcon />
        </IconButton>
      }
    >
      <ExplorerTreeRoot
        placeholder={<RootEmpty onClickCreate={handleCreateCollection} />}
      >
        {collections.map(collection => (
          <ExplorerCollectionNode
            key={collection.id}
            collectionId={collection.id}
            reorderable={false}
            location={{
              at: 'explorer:collection:list',
            }}
          />
        ))}
      </ExplorerTreeRoot>
    </CollapsibleSection>
  );
};
